import { useState, type FormEvent } from 'react'
import { KeyRound } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Button, Field, Input } from '@/components/ui/primitives'
import { useToast } from '@/components/ui/toast'
import { ThemeToggle } from '@/app/ThemeToggle'
import { AuthFrame } from '@/features/auth/AuthFrame'

export function LoginPage() {
  const { signIn } = useAuth()
  const toast = useToast()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [forgot, setForgot] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn(username.trim(), password)
    } catch (caught) {
      // The server deliberately returns the same message for an unknown user
      // and a wrong password, so this endpoint cannot be used to find out
      // which accounts exist. Show exactly what it said.
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not sign in. Check your connection and try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  async function onForgot(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      const response = await api.post<{ message: string }>('/auth/forgot-password', {
        email: username.trim(),
      })
      toast.success('Check your inbox', response.message)
      setForgot(false)
    } catch {
      toast.error('Could not send the reset link', 'Try again in a moment.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthFrame
      titleAs="h1"
      subtitle="Guest registration"
      action={<ThemeToggle />}
      footer={
        <>
          Guest tablets pair at <span className="font-mono">/kiosk</span> with a code from an
          administrator — they do not sign in.
        </>
      }
    >
      {forgot ? (
        <form onSubmit={onForgot} className="flex flex-col gap-4">
          <div>
            <h2 className="text-[20px] font-semibold tracking-[-0.01em]">
              Reset your password
            </h2>
            <p className="mt-1 text-[13px] text-[var(--text-muted)]">
              Enter your email address and we will send you a link.
            </p>
          </div>
          <Field label="Email address" htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </Field>
          <Button type="submit" variant="primary" loading={busy}>
            Send reset link
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setForgot(false)}>
            Back to sign in
          </Button>
        </form>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="mb-1">
            <h2 className="text-[20px] font-semibold tracking-[-0.01em]">Welcome back</h2>
            <p className="mt-1 text-[13px] text-[var(--text-muted)]">
              Sign in with the account your administrator gave you.
            </p>
          </div>

          <Field label="Username" htmlFor="username">
            <Input
              id="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              invalid={Boolean(error)}
              required
            />
          </Field>

          <Field label="Password" htmlFor="password">
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              invalid={Boolean(error)}
              required
            />
          </Field>

          {error && (
            <p role="alert" className="text-[13px] text-[var(--danger)]">
              {error}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={busy}
            icon={<KeyRound className="h-4 w-4" />}
            className="mt-1"
          >
            Sign in
          </Button>

          <button
            type="button"
            onClick={() => setForgot(true)}
            className="text-[13px] text-[var(--brand)] hover:underline"
          >
            Forgot your password?
          </button>
        </form>
      )}
    </AuthFrame>
  )
}
